import { useParams, useNavigate } from 'react-router-dom'
import { IoArrowBack, IoDownload, IoLocation, IoCard, IoCheckmark, IoTime } from 'react-icons/io5'

import Button from '@/components/common/Button/Button'
import Spinner from '@/components/common/Spinner/Spinner'
import { useOrder } from '@/hooks/useOrders'
import { sampleProductImage } from '@/utils/constants/common'
import routes from '@/routes/routes'

import './OrderDetail.css'

const OrderDetail = () => {
    const { orderId } = useParams<{ orderId: string }>()
    const navigate = useNavigate()
    const { data: order, isLoading, error } = useOrder(Number(orderId))

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        })
    }

    const formatPrice = (price: number) => {
        return `$${price.toFixed(2)}`
    }

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'pending': return 'warning'
            case 'processing': return 'info'
            case 'shipped': return 'primary'
            case 'delivered': return 'success'
            case 'cancelled': return 'danger'
            default: return 'secondary'
        }
    }

    const getOrderTimeline = () => {
        if (!order) return []

        const timeline = [
            {
                status: 'pending',
                label: 'Order Placed',
                date: order.created_at,
                completed: true,
            },
            {
                status: 'processing',
                label: 'Processing',
                date: order.updated_at, // Use updated_at as processing date
                completed: ['confirmed', 'in_production', 'quality_check', 'shipped', 'delivered'].includes(order.status),
            },
            {
                status: 'shipped',
                label: 'Shipped',
                date: order.updated_at, // Use updated_at as shipped date
                completed: ['shipped', 'delivered'].includes(order.status),
            },
            {
                status: 'delivered',
                label: 'Delivered',
                date: order.estimated_delivery_date || order.updated_at,
                completed: order.status === 'delivered',
            },
        ]

        return timeline
    }

    if (isLoading) {
        return (
            <div className="order_detail_loading">
                <Spinner size="lg" />
                <p>Loading order details...</p>
            </div>
        )
    }

    if (error || !order) {
        return (
            <div className="order_detail_error">
                <h2>Order not found</h2>
                <p>The order you're looking for doesn't exist or you don't have permission to view it.</p>
                <Button variant="primary" onClick={() => navigate(routes.orders)}>
                    Back to Orders
                </Button>
            </div>
        )
    }

    return (
        <div className="order_detail_container">
            <div className="order_detail_header">
                <Button
                    variant="secondary"
                    onClick={() => navigate(routes.orders)}
                >
                    <IoArrowBack size={16} />
                    Back to Orders
                </Button>

                <div className="order_detail_title">
                    <h1>Order #{order.order_number}</h1>
                    <span className={`order_status status_${getStatusColor(order.status)}`}>
                        {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                    </span>
                </div>

                {(order as any).invoice_url && (
                    <Button
                        variant="secondary"
                        onClick={() => window.open((order as any).invoice_url, '_blank')}
                    >
                        <IoDownload size={16} />
                        Download Invoice
                    </Button>
                )}
            </div>

            <div className="order_detail_content">
                <div className="order_detail_main">
                    {/* Order Timeline */}
                    <div className="order_timeline_section">
                        <h3>Order Status</h3>
                        <div className="order_timeline">
                            {getOrderTimeline().map((step) => (
                                <div
                                    key={step.status}
                                    className={`timeline_step ${step.completed ? 'completed' : 'pending'}`}
                                >
                                    <div className="timeline_icon">
                                        {step.completed ? (
                                            <IoCheckmark size={16} />
                                        ) : (
                                            <IoTime size={16} />
                                        )}
                                    </div>
                                    <div className="timeline_content">
                                        <h4>{step.label}</h4>
                                        {step.date && (
                                            <p>{formatDate(step.date)}</p>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Order Items */}
                    <div className="order_items_section">
                        <h3>Items Ordered</h3>
                        <div className="order_items_list">
                            {order.items.map((item, index) => (
                                <div key={index} className="order_item_detail">
                                    <img
                                        src={item.product.main_image_url || sampleProductImage}
                                        alt={item.product.name}
                                        className="item_image"
                                    />
                                    <div className="item_info">
                                        <h4>{item.product.name}</h4>
                                        <p className="item_description">{item.product.description}</p>
                                        
                                        {(item as any).customization && (
                                            <div className="item_customization">
                                                <h5>Customization:</h5>
                                                {(item as any).customization.material && (
                                                    <p><strong>Material:</strong> {(item as any).customization.material}</p>
                                                )}
                                                {(item as any).customization.color && (
                                                    <p><strong>Color:</strong> {(item as any).customization.color}</p>
                                                )}
                                                {(item as any).customization.size && (
                                                    <p><strong>Size:</strong> {(item as any).customization.size}</p>
                                                )}
                                                {(item as any).customization.custom_text && (
                                                    <p><strong>Custom Text:</strong> {(item as any).customization.custom_text}</p>
                                                )}
                                                {(item as any).customization.notes && (
                                                    <p><strong>Notes:</strong> {(item as any).customization.notes}</p>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <div className="item_pricing">
                                        <div className="item_quantity">Qty: {item.quantity}</div>
                                        <div className="item_price">{formatPrice(item.unit_price)}</div>
                                        <div className="item_total">{formatPrice(item.total_price)}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="order_detail_sidebar">
                    {/* Order Summary */}
                    <div className="order_summary_section">
                        <h3>Order Summary</h3>
                        <div className="summary_details">
                            <div className="summary_row">
                                <span>Subtotal:</span>
                                <span>{formatPrice(order.subtotal)}</span>
                            </div>
                            {(order as any).discount_amount && (order as any).discount_amount > 0 && (
                                <div className="summary_row discount">
                                    <span>Discount:</span>
                                    <span>-{formatPrice((order as any).discount_amount)}</span>
                                </div>
                            )}
                            <div className="summary_row">
                                <span>Shipping:</span>
                                <span>{order.shipping_cost > 0 ? formatPrice(order.shipping_cost) : 'Free'}</span>
                            </div>
                            <div className="summary_row">
                                <span>Tax:</span>
                                <span>{formatPrice(order.tax_amount)}</span>
                            </div>
                            <div className="summary_row total">
                                <span>Total:</span>
                                <span>{formatPrice(order.total_amount)}</span>
                            </div>
                        </div>
                    </div>

                    {/* Shipping Address */}
                    <div className="shipping_address_section">
                        <h3>
                            <IoLocation size={18} />
                            Shipping Address
                        </h3>
                        <div className="address_details">
                            <p>{order.shipping_address.full_name}</p>
                            <p>{order.shipping_address.address_line_1}</p>
                            {order.shipping_address.address_line_2 && (
                                <p>{order.shipping_address.address_line_2}</p>
                            )}
                            <p>
                                {order.shipping_address.city}, {order.shipping_address.state_province} {order.shipping_address.postal_code}
                            </p>
                            <p>{order.shipping_address.country}</p>
                        </div>
                    </div>

                    {/* Payment Method */}
                    {(order as any).payment_method && (
                        <div className="payment_method_section">
                            <h3>
                                <IoCard size={18} />
                                Payment Method
                            </h3>
                            <div className="payment_details">
                                <p>{(order as any).payment_method.type}</p>
                                {(order as any).payment_method.last_four && (
                                    <p>**** **** **** {(order as any).payment_method.last_four}</p>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Tracking Information */}
                    {order.tracking_number && (
                        <div className="tracking_section">
                            <h3>Tracking Information</h3>
                            <div className="tracking_details">
                                <p><strong>Tracking Number:</strong></p>
                                <p className="tracking_number">{order.tracking_number}</p>
                                {(order as any).tracking_url && (
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={() => window.open((order as any).tracking_url, '_blank')}
                                    >
                                        Track Package
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Actions */}
                    <div className="order_actions_section">
                        {order.status === 'delivered' && (
                            <Button
                                variant="primary"
                                onClick={() => navigate(`${routes.products}?reorder=${order.id}`)}
                            >
                                Reorder Items
                            </Button>
                        )}
                        
                        {['pending', 'processing'].includes(order.status) && (
                            <Button
                                variant="danger"
                                onClick={() => {
                                    // Handle order cancellation
                                    console.log('Cancel order:', order.id)
                                }}
                            >
                                Cancel Order
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default OrderDetail
